import { useMemo, type ReactNode } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';

/**
 * NOTE: Do NOT pass stylis `prefixer` into stylisPlugins.
 * Emotion already prefixes by default when stylisPlugins is omitted; when we
 * pass plugins we replace that list. Including `prefixer` again crashes on
 * MUI `&::placeholder` rules (Select/TextField): "Cannot read properties of
 * undefined (reading 'push')".
 */
const stylisRtl =
  typeof rtlPlugin === 'function'
    ? rtlPlugin
    : (rtlPlugin as unknown as { default: typeof rtlPlugin }).default;

const cacheLtr = createCache({ key: 'muiltr' });
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [stylisRtl],
});

/** Emotion cache that flips margins/paddings/borders for Arabic & other RTL languages. */
export function RtlCacheProvider({ direction, children }: { direction: 'ltr' | 'rtl'; children: ReactNode }) {
  const cache = useMemo(() => (direction === 'rtl' ? cacheRtl : cacheLtr), [direction]);
  return <CacheProvider value={cache}>{children}</CacheProvider>;
}

export const RTL_LANGUAGE_CODES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'dv']);

export function isRtlLanguage(code: string): boolean {
  return RTL_LANGUAGE_CODES.has(String(code || '').toLowerCase().trim());
}
